import { envConfigs } from '@config/envConfigs';
import { IFileResponse, IPdfFileDownloadRequest } from '@file/projectFile/projectFile.models';
import { apiServerGet, apiServerPost } from '@common/axios.services';
import { AxiosRequestConfig } from 'axios';
import CryptoJS from 'crypto-js';
import { IDownloadAttachmentRequest, IDownloadFileProgressProps } from './file.models';

export const apiRoutes = {
  downloadAttachment: (id: number) => `/Files/${id}/DownloadAttachment`,
};

const createDownloadFileWithCustomPropsAsync = <PropsType extends IDownloadFileProgressProps & { projectId?: number }>(
  apiRoute: string | ((props: PropsType) => string),
  type: 'POST' | 'GET',
) => {
  return async (props: PropsType): Promise<IFileResponse> => {
    const { onDownloadProgressUpdate, ...request } = props;

    const route = `${envConfigs.apiBaseUrl}${typeof apiRoute === 'function' ? apiRoute(props) : apiRoute}`;

    const baseConfig: AxiosRequestConfig<any> = {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (onDownloadProgressUpdate) {
          const { total, loaded } = progressEvent;

          const percentCompleted = Math.floor((loaded / total) * 100);
          onDownloadProgressUpdate(percentCompleted);
        }
      },
    };

    const fileResponse =
      type === 'GET'
        ? await apiServerGet<Blob>(route, {
            ...baseConfig,
            params: {
              ...request,
            },
          })
        : await apiServerPost<Blob>(route, request, {
            ...baseConfig,
            params: {
              projectId: props.projectId,
            },
          });

    let filename = '';
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(fileResponse.headers['content-disposition']);
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '');
    }
    return { file: fileResponse.data, filename, mimeType: fileResponse.headers['content-type'] };
  };
};

export const createDownloadFileWithCustomPropsAsyncFnGet = <PropsType extends IDownloadFileProgressProps>(
  apiRoute: string | ((props: PropsType) => string),
) => {
  return createDownloadFileWithCustomPropsAsync(apiRoute, 'GET');
};

export const createDownloadFileWithCustomPropsAsyncFn = <PropsType extends IDownloadFileProgressProps>(
  apiRoute: string | ((props: PropsType) => string),
) => {
  return createDownloadFileWithCustomPropsAsync(apiRoute, 'POST');
};

export const downloadAttachmentAsync = createDownloadFileWithCustomPropsAsyncFn<IDownloadAttachmentRequest>(
  (props: IDownloadAttachmentRequest) => apiRoutes.downloadAttachment(props.id),
);

export const createDownloadFileAsyncFn = async (
  route: string,
  request: IPdfFileDownloadRequest,
): Promise<Promise<IFileResponse>> => {
  const fileResponse = await apiServerGet<Blob>(`${envConfigs.apiBaseUrl}${route}`, {
    params: {
      projectId: request.projectId,
    },
    responseType: 'blob',
    onDownloadProgress: (progressEvent) => {
      if (request.onDownloadProgressUpdate) {
        const { total, loaded } = progressEvent;

        const percentCompleted = Math.floor((loaded / total) * 100);
        request.onDownloadProgressUpdate(percentCompleted);
      }
    },
  });

  let filename = '';
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(fileResponse.headers['content-disposition']);
  if (matches != null && matches[1]) {
    filename = matches[1].replace(/['"]/g, '');
  }
  return { file: fileResponse.data, filename };
};

export const getFileMD5 = (file: File, cbProgress?: (int: number) => void): Promise<string> => {
  return new Promise((resolve, reject) => {
    const md5 = CryptoJS.algo.MD5.create();
    readFileAsChunked(
      file,
      (chunk, offs, total) => {
        md5.update(CryptoJS.enc.Latin1.parse(chunk));
        if (cbProgress) {
          cbProgress(offs / total);
        }
      },
      (err) => {
        if (err) {
          reject(err);
        } else {
          const hash = md5.finalize();
          const hashHex = hash.toString(CryptoJS.enc.Hex);
          resolve(hashHex);
        }
      },
    );
  });
};

const readFileAsChunked = (
  file: Blob,
  chunkCallback: (readerResult: string, offset: number, fileSize: number) => void,
  endCallback: (error: any) => void,
  readNextCallBack?: (fileReader: FileReader, fileSlice: Blob) => void,
) => {
  const fileSize = file.size;
  const chunkSize = 4 * 1024 * 1024; // 4MB
  let offset = 0;

  const reader = new FileReader();
  reader.onload = () => {
    if (reader.error) {
      endCallback(reader.error || {});
      return;
    }
    offset += (reader.result as string).length;
    // callback for handling read chunk
    chunkCallback(reader.result as string, offset, fileSize);
    if (offset >= fileSize) {
      endCallback(null);
      return;
    }
    readNext();
  };

  reader.onerror = (err) => {
    endCallback(err || {});
  };

  const readNext = () => {
    const fileSlice = file.slice(offset, offset + chunkSize);
    if (!readNextCallBack) reader.readAsBinaryString(fileSlice);
    else readNextCallBack(reader, fileSlice);
  };
  readNext();
};

export const convertRequestToFormData = (request: object, shouldIgnoreEmpty = true): FormData => {
  const formData = new FormData();
  Object.entries(request).forEach(([key, value]) => {
    if (shouldIgnoreEmpty && (value === undefined || value === null)) return;
    formData.append(key, value);
  });
  return formData;
};
